import { createContext, useContext } from "react";
import { DEFAULT_MUSIC, type MusicKey, TRACKS } from "./music";

/**
 * B2B partner video — timing.
 *
 * Everything is authored in beats of the chosen soundtrack (`music.ts`).
 * A beat is rarely a whole number of frames (121 BPM = 14.88f at 30 fps),
 * so cut frames are rounded from the *cumulative* beat count: every cut
 * stays within half a frame of the music, with no drift over the video.
 *
 * Holds follow the B2C rule (≥ ~1.5s + 0.3s per word once a line has
 * landed). LinkedIn plays muted by default, so the text carries the story:
 * lengthen a scene here rather than cutting a hold short.
 */

export const B2B_FPS = 30;

/**
 * Scene lengths, in beats. Order = playback order. Every length is a whole
 * number of bars (4 beats), so every cut lands on a downbeat, where the
 * music changes chord / section, not just on any beat.
 */
export const SCENE_BEATS = {
  hook: 8, //     CAC +60 % in 5 years (Paddle) — the pain, in the first 3s
  dark: 12, //    "Your customers already recommend you" → "But you can't see it" (direct traffic)
  reveal: 8, //   Frak turns your customers into an acquisition channel (music opens here, beat 20)
  setCac: 8, //   Step 1 — set your acquisition cost upfront (80 / 20 split)
  share: 8, //    Step 2 — customers share a tracked link (order confirmation widget)
  track: 12, //   Step 3 — every sale attributed, ambassadors paid automatically
  pricing: 8, //  20 % on sales only, no subscription / setup / commitment, CMS (music steps up, beat 56)
  proof: 12, //   Up to 40 % of sales, −26 % CAC (internal) + live brands
  cta: 8, //      Founding Brands — frak.id/partner (music peaks, beat 80)
} as const;

export type SceneKey = keyof typeof SCENE_BEATS;

export const SCENE_ORDER = Object.keys(SCENE_BEATS) as SceneKey[];

const TOTAL_BEATS = SCENE_ORDER.reduce((sum, key) => sum + SCENE_BEATS[key], 0);

export type Timing = {
  /** Frames per beat (fractional). */
  beat: number;
  /** Frame of a beat index from the start of the video. */
  at: (beats: number) => number;
  start: Record<SceneKey, number>;
  frames: Record<SceneKey, number>;
  duration: number;
};

export const timingFor = (music: MusicKey): Timing => {
  const beat = (B2B_FPS * 60) / TRACKS[music].bpm;
  const at = (beats: number) => Math.round(beats * beat);
  const start = {} as Record<SceneKey, number>;
  const frames = {} as Record<SceneKey, number>;
  let beats = 0;
  for (const key of SCENE_ORDER) {
    start[key] = at(beats);
    beats += SCENE_BEATS[key];
    frames[key] = at(beats) - start[key];
  }
  return { beat, at, start, frames, duration: at(TOTAL_BEATS) };
};

export const TimingContext = createContext<Timing>(timingFor(DEFAULT_MUSIC));

/** The current soundtrack's timing (inside `B2BVideo`). */
export const useTiming = (): Timing => useContext(TimingContext);
